import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt';
import { Typography } from '@mui/material';
import PaiButton from 'features/ui/base-components/pai-button';
import PaiDropzone from 'features/ui/base-components/pai-dropzone';
import _ from 'lodash';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import { ContactSearchMethods, ContactToAdd, SelectorIds } from '../../../campaign-builder.types';
import ManualContactList from './manual-contact-list/manual-contact-list';

interface Props {
  contactSearchMethod: ContactSearchMethods | '';
  contactsToAdd: ContactToAdd[];
  addContactsLoading: boolean;

  onFileDrop(acceptedFiles: any): void;

  handleFileChange(event: React.ChangeEvent<HTMLInputElement>): void;

  handleDeleteContacts(): void;

  handleAddContactsFromFile(): void;
}

const UploadContactList: React.FC<Props> = ({
  contactSearchMethod,
  contactsToAdd,
  addContactsLoading,
  onFileDrop,
  handleFileChange,
  handleDeleteContacts,
  handleAddContactsFromFile,
}) => {
  const { classes, cx } = useStyles();
  const { t } = useTranslation();

  const areContactsAdded = !_.isEmpty(contactsToAdd);

  const scrollToSection = (section: SelectorIds) => {
    return () => {
      const scrollContainer = document.getElementById(SelectorIds.STEP_CONTENT);
      if (!_.isNil(scrollContainer)) {
        const targetSection = document.getElementById(section);

        if (!_.isNil(targetSection)) {
          const scrollOffset = targetSection.offsetTop - scrollContainer.offsetTop;

          scrollContainer.scrollTo({
            top: scrollOffset,
            behavior: 'smooth',
          });
        }
      }
    };
  };

  useEffect(() => {
    if (areContactsAdded) {
      scrollToSection(SelectorIds.FILE_UPLOAD_ADD_CONTACTS)();
    }
  }, [areContactsAdded]);

  return (
    <>
      <section id={SelectorIds.CONTACT_LIST} className={cx(classes.section, 'border-bottom-disabled')}>
        <ArrowRightAltIcon className={cx(classes.arrow, contactSearchMethod ? 'active' : undefined)} />
        <Typography className={classes.subTitle} mb={'8px'}>
          {t('addExistingContacts')}
        </Typography>
        <Typography className={classes.greyText} mb={'24px'}>
          {t('uploadInstructions')}
        </Typography>
        {_.isEmpty(contactsToAdd) ? (
          <PaiDropzone maxWidth={'100%'} height={'200px'} onDrop={onFileDrop} />
        ) : (
          <ManualContactList contactsToAdd={contactsToAdd} handleFileChange={handleFileChange} handleDeleteContacts={handleDeleteContacts} />
        )}
      </section>
      {!_.isEmpty(contactsToAdd) ? (
        <section id={SelectorIds.FILE_UPLOAD_ADD_CONTACTS} className={cx(classes.section, 'last-section')}>
          <Typography className={classes.title} mb={'8px'}>
            {t('almostThere')}
          </Typography>
          <Typography className={classes.greyText} mb={'16px'}>
            {t('leadReadyMessage')}
          </Typography>
          <PaiButton text={t('addToLeadBank')} border={'rounded'} loading={addContactsLoading} action={handleAddContactsFromFile} />
        </section>
      ) : null}
    </>
  );
};

const useStyles = makeStyles()((theme) => ({
  section: {
    position: 'relative',
    padding: '24px 48px',
    borderBottom: `1px solid ${theme.palette.greys.lighter}`,
    '&.border-bottom-disabled': {
      borderBottom: 'none',
    },
    '&.last-section': {
      margin: '0 -48px',
      padding: '24px calc(48px + 48px)',
      borderTop: `1px solid ${theme.palette.greys.lighter}`,
      borderBottom: 'none',
    },
  },
  arrow: {
    display: 'none',
    position: 'absolute',
    top: '18px',
    left: '0px',
    fontSize: theme.typography.pxToRem(32),
    color: theme.palette.primary.main,
    '&.active': {
      display: 'inline-block',
    },
  },
  title: {
    fontSize: theme.typography.pxToRem(20),
    fontWeight: 'bold',
  },
  subTitle: {
    fontSize: theme.typography.pxToRem(15),
    fontWeight: 'bold',
  },
  greyText: {
    fontSize: theme.typography.pxToRem(15),
    color: theme.palette.greys.medium,
  },
}));

export default UploadContactList;
